/** Batch "Flush queues" dialog for the api-requests grid — the console
 *  counterpart of the `apireq queues` management command's default path.
 *
 *  For each selected request the stored body is parsed into bookings; every
 *  non-empty Redis booking queue among them gets its processing lock set and
 *  a `process_booking_queue` task dispatched. Nothing is deleted — this only
 *  kicks processing of queues that got stuck (e.g. after a worker crash).
 */
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';

import type { BatchActionContext } from '@/components/DataGrid/batchTypes';

export function FlushQueuesBatchDialog({ ctx }: { ctx: BatchActionContext }) {
  const submit = () => {
    void ctx.runStandard({});
    ctx.close();
  };

  return (
    <Dialog open onClose={ctx.close} fullWidth maxWidth="sm">
      <DialogTitle>Flush booking queues</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant="body2">
            Each selected request&apos;s stored body is parsed into bookings, and every
            non-empty Redis booking queue among them is kicked: the processing lock is set and
            a <code>process_booking_queue</code> task is scheduled. Use this when webhook
            bookings appear stuck in their queues.
          </Typography>
          <Alert severity="info">
            Nothing is deleted — queue entries are processed, not discarded. Requests without
            an integration are skipped. Capped at 500 rows per batch; runs in the background.
          </Alert>
          {ctx.selection.mode === 'all_matching' && (
            <Alert severity="info">
              Acting on {ctx.matchedPhrase} requests (up to the 500-row
              cap).
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={ctx.close}>Cancel</Button>
        <Button variant="contained" onClick={submit}>
          Flush queues for {ctx.count.toLocaleString()} request(s)
        </Button>
      </DialogActions>
    </Dialog>
  );
}
